// betmgm-client.js — generates injectable JS fetch script and parses bridge messages
// Spec: docs/specs/slipsync-ui.md Section 6 (injectable script behavior)

import { getBetmgmApiUrl, VALID_STATES, isBridgeNonce } from './constants';
import { CAPTURE_MEDIA_TYPES, CAPTURE_CHARSETS } from './response-intercept';
import { BOOK_ANSWER_REASONS, CONTENT_TYPE_CLASSES } from './attempt-exit-map';

// Returns JS source string for executeScript({ code }). Runs inside WebView
// using credentials: 'include'. Posts results via window.mobileApp.postMessage.
// Two calls, index pagination: OPEN first, then SETTLED, into one list de-duplicated by betSlipNumber.
// BetMGM blocks the combined typeFilter+pinnedBetslipIds query (302 -> /Account/AccessDenied), verified
// 2026-10-18; see getBetmgmApiUrl. Lockout insurance: MAX_PAGES (10) caps the requests across BOTH calls
// (OPEN at most OPEN_BUDGET = 6, SETTLED the rest, at least 4), 500 ms apart -- never more requests than the
// single-call script sent. A cap that ends a read while pages remain posts partial (stoppedAt 'cap').
// #1409: every message carries the bridge's per-open nonce; the bridge drops a message without it (a subframe or
// another document posting to the native handler). No nonce, no script: an unbound import script is never built.
export function getBetmgmFetchScript(state, nonce) {
  if (!VALID_STATES.includes(state)) throw new Error(`Invalid state: ${state}`);
  if (!isBridgeNonce(nonce)) throw new Error('Invalid bridge nonce');

  return `(async function() {
  var NONCE = ${JSON.stringify(nonce)};
  // GitHub Codex P1 (02b25c3f): the bridge's URL can trail the WebView, so a not-signed-in answer carries the live
  // route class at the moment it is posted. A sign-in segment wins over a bet-history one. Only the class leaves.
  var LIVE_AUTH = ['login', 'signin', 'sign-in', 'auth', 'verify', 'verification'];
  var LIVE_HISTORY = ['my-bets', 'mybets'];
  function liveRoute() {
    try {
      var p = location.pathname.toLowerCase().split('/');
      var h = false;
      for (var q = 0; q < p.length; q++) {
        if (LIVE_AUTH.indexOf(p[q]) !== -1) return 'signin';
        if (LIVE_HISTORY.indexOf(p[q]) !== -1) h = true;
      }
      return h ? 'history' : 'other';
    } catch (e) { return 'other'; }
  }
  function post(d) { if (d.type === 'auth_error') d.route = liveRoute(); d.nonce = NONCE; window.mobileApp.postMessage({ detail: d }); }
  try {
    var collected = [];
    var MAX_BETSLIPS = 50;
    var MAX_PAGES = 10;
    var ITEMS_PER_PAGE = 6;
    var partial = false;
    var partialReason = null;
    // 2026-10-18 visibility: a not-signed-in answer carries what the book answered -- the HTTP status, the
    // content-type CLASS (never the header) and, for a JSON body without betslips, its top-level key NAMES
    // (never values). Reported only: no branch below reads them.
    function ctClass(r) {
      try {
        var c = String((r.headers && r.headers.get('content-type')) || '').toLowerCase();
        if (!c) return 'none';
        if (c.indexOf('json') !== -1) return 'json';
        if (c.indexOf('html') !== -1) return 'html';
        if (c.indexOf('text/') === 0) return 'text';
        return 'other';
      } catch (e) { return 'none'; }
    }
    function keyNames(b) {
      var out = [];
      if (!b || typeof b !== 'object' || Array.isArray(b)) return out;
      var ks = Object.keys(b);
      // A letter first: digit-only keys (an id-keyed map) are values in disguise and are dropped.
      for (var q = 0; q < ks.length && out.length < 10; q++) { if (/^[A-Za-z][A-Za-z0-9_]{0,39}$/.test(ks[q])) out.push(ks[q]); }
      return out;
    }
    // A followed redirect is a coded answer, not bets. Only BetMGM's /Account/AccessDenied (where it sends a blocked
    // query) is access_denied; any other redirect is 'redirected'.
    function redirectReason(r) {
      if (String(r.url || '').indexOf('/Account/AccessDenied') !== -1) return 'access_denied';
      return r.redirected === true ? 'redirected' : null;
    }
    // One list across both calls, keyed by betSlipNumber. A bet that settled between the two calls appears in both:
    // the SETTLED copy replaces the open one in place, so it keeps its result. Inside one call the first copy wins.
    var seen = {};
    var capped = false;
    function add(list, settledCall) {
      for (var j = 0; j < list.length; j++) {
        var s = list[j];
        var n = s && (typeof s.betSlipNumber === 'string' || typeof s.betSlipNumber === 'number') ? '#' + s.betSlipNumber : null;
        if (n !== null && seen[n]) {
          if (settledCall && seen[n].from === 'open') { collected[seen[n].at] = s; seen[n].from = 'settled'; }
          continue;
        }
        if (collected.length >= MAX_BETSLIPS) { capped = true; break; }
        if (n !== null) seen[n] = { at: collected.length, from: settledCall ? 'settled' : 'open' };
        collected.push(s);
      }
    }
    var OPEN_BUDGET = 6;
    var CALLS = [
      { name: 'open', url: ${JSON.stringify(getBetmgmApiUrl(state, 'open', 1))} },
      { name: 'settled', url: ${JSON.stringify(getBetmgmApiUrl(state, 'settled', 1))} }
    ];
    var requests = 0;
    var res = null;

    for (var c = 0; c < CALLS.length && !partial; c++) {
      // A failure on the settled call keeps the open bets (partial); on the open call, today's codes.
      var settled = CALLS[c].name === 'settled';
      // OPEN may use at most OPEN_BUDGET requests; SETTLED gets whatever is left of MAX_PAGES (at least 4).
      var limit = settled ? MAX_PAGES - requests : Math.min(OPEN_BUDGET, MAX_PAGES);
      for (var p = 1; ; p++) {
        // A cap reached with pages still to read: the result is partial ('cap'), never a full read.
        if (collected.length >= MAX_BETSLIPS || p > limit) { capped = true; break; }
        if (requests > 0) await new Promise(function(r) { setTimeout(r, 500); });
        // "First" means no bets read yet, not the first request (GitHub Codex P1, 51a011a0): after an empty OPEN read,
        // a SETTLED failure is still a not-signed-in answer (the user may be in sign-in/2FA by now), never terminal.
        var first = collected.length === 0;
        var url = CALLS[c].url.replace('index=1', 'index=' + p);
        requests++;

        try {
          res = await fetch(url, { credentials: 'include', headers: { 'Accept': 'application/json', 'x-bwin-sports-api': 'prod' } });
        } catch (e) {
          if (first) {
            // Bridge hardening: before sign-in a redirect aborts the fetch. Not signed in yet, keep waiting.
            post({ type: 'auth_error', reason: 'network_error', status: null, ctype: null });
            return;
          }
          partial = true; partialReason = settled ? 'settled_fetch_error' : 'fetch_error';
          break;
        }

        var redirected = redirectReason(res);
        if (redirected) {
          if (first) {
            post({ type: 'auth_error', reason: redirected, status: res.status, ctype: ctClass(res) });
            return;
          }
          partial = true; partialReason = settled ? 'settled_fetch_error' : 'fetch_error';
          break;
        }

        if (res.status === 401 || res.status === 403) {
          if (first) {
            post({ type: 'auth_error', reason: 'http_status', status: res.status, ctype: ctClass(res) });
            return;
          }
          // Pre-Codex: a 401/403 after a good first page (session expiry mid-paging) keeps the bets already read.
          partial = true; partialReason = settled ? 'settled_fetch_error' : 'fetch_error';
          break;
        }

        if (!res.ok) {
          if (first) {
            // Bridge hardening: BetMGM answers 400/500 before sign-in (2026-10-12 lockout). Keep waiting.
            post({ type: 'auth_error', reason: 'http_error', status: res.status, ctype: ctClass(res) });
            return;
          }
          partial = true; partialReason = settled ? 'settled_fetch_error' : 'fetch_error';
          break;
        }

        var body;
        try { body = await res.json(); } catch (e) {
          if (first) {
            // 200 + non-JSON = HTML login page (BetMGM returns text/html for both).
            // Post auth_error so the WebView stays open for user login.
            post({ type: 'auth_error', reason: 'html_response', status: res.status, ctype: ctClass(res) });
            return;
          }
          partial = true; partialReason = settled ? 'settled_fetch_error' : 'parse_error';
          break;
        }

        var slips = body && body.betslips;
        if (!Array.isArray(slips)) {
          if (first) {
            // Bridge hardening: an error body instead of betslips before sign-in. Keep waiting.
            post({ type: 'auth_error', reason: 'unexpected_shape', status: res.status, ctype: ctClass(res), keys: keyNames(body) });
            return;
          }
          partial = true; partialReason = settled ? 'settled_fetch_error' : 'parse_error';
          break;
        }

        add(slips, settled);
        if (slips.length < ITEMS_PER_PAGE) break;
      }
    }

    // Bridge hardening (P2-9): an empty first page while the page is still a sign-in route is not a real
    // empty result. Keep waiting.
    var AUTH_ROUTES = ['login', 'signin', 'sign-in', 'auth', 'verify', 'verification'];
    // Any path segment counts (BetMGM's login has lived at /en/labelhost/login).
    var segs = location.pathname.toLowerCase().split('/');
    var onAuthRoute = false;
    for (var k = 0; k < segs.length; k++) { if (AUTH_ROUTES.indexOf(segs[k]) !== -1) { onAuthRoute = true; break; } }
    if (!partial && collected.length === 0 && onAuthRoute) {
      post({ type: 'auth_error', reason: 'auth_route_empty', status: res.status, ctype: ctClass(res) });
      return;
    }

    if (!partial && capped && collected.length > 0) { partial = true; partialReason = 'cap'; }
    if (partial && collected.length > 0) {
      post({ type: 'partial', data: collected, stoppedAt: partialReason });
    } else if (partial && collected.length === 0) {
      // Settled unreadable after a good (empty) open read: signed in, so a definite book error.
      post({ type: 'api_error', error: partialReason === 'settled_fetch_error' ? partialReason : partialReason + ' on first page', page: 0 });
    } else {
      post({ type: 'betslips', data: collected });
    }
  } catch (e) {
    post({ type: 'script_error', error: e.message || String(e) });
  }
})();`;
}

// Parse event.detail from messageFromWebview. Handles undefined (RISK 1).
// #1712 PR A (spec docs/superpowers/specs/2026-11-01-1712-dk-open-bet-probe-design.md §3.2): single job --
// validate a DK open-probe object from the page and rebuild it from known fields, or return null. Same rules
// as the server's validateOpenProbe (api/lib/slipsync/dk-open-probe.js); a parity test pins them together.
const OPEN_PROBE_WORDS = ['Open', 'Unsettled', 'Pending'];
const OPEN_PROBE_OUTCOMES = ['frame', 'rpc_error', 'rpc_error_unattributed', 'not_array', 'timeout', 'socket_closed'];
const isPlainObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isInt32 = (v) => Number.isInteger(v) && v >= -2147483648 && v <= 2147483647;
const isProbeCount = (v) => Number.isInteger(v) && v >= 0 && v <= 1000;
function parseOpenProbeAttempt(a, seen) {
  if (!isPlainObj(a) || !OPEN_PROBE_WORDS.includes(a.word) || seen.has(a.word) || !OPEN_PROBE_OUTCOMES.includes(a.outcome)) return null;
  seen.add(a.word);
  const out = { word: a.word, outcome: a.outcome };
  if (a.code !== undefined) {
    if (!isInt32(a.code)) return null;
    out.code = a.code;
  }
  const isFrame = a.outcome === 'frame';
  if (isFrame !== (a.count !== undefined) || (isFrame && !isProbeCount(a.count))) return null;
  if (isFrame) out.count = a.count;
  if (a.bets !== undefined) {
    if (!isFrame || !Array.isArray(a.bets) || a.bets.length > 10) return null;
    out.bets = a.bets.filter(isPlainObj);
  }
  if (a.result !== undefined) {
    if (a.outcome !== 'not_array' || !isPlainObj(a.result)) return null;
    out.result = a.result;
  }
  return out;
}
export function parseOpenProbe(raw) {
  try {
    if (!isPlainObj(raw) || !Array.isArray(raw.attempts) || raw.attempts.length < 1 || raw.attempts.length > 3) return null;
    const seen = new Set();
    const attempts = [];
    for (const a of raw.attempts) {
      const v = parseOpenProbeAttempt(a, seen);
      if (!v) return null;
      attempts.push(v);
    }
    const probe = { attempts };
    if (raw.unattributed !== undefined) {
      if (!Array.isArray(raw.unattributed) || raw.unattributed.length > 3 || !raw.unattributed.every(isInt32)) return null;
      probe.unattributed = raw.unattributed.slice();
    }
    if (raw.error !== undefined) {
      if (raw.error !== 'probe_exception') return null;
      probe.error = raw.error;
    }
    if (raw.betsDropped !== undefined) {
      if (!isProbeCount(raw.betsDropped)) return null;
      probe.betsDropped = raw.betsDropped;
    }
    return probe;
  } catch (e) {
    return null; // a hostile getter: not a probe (the caller records openProbeDropped)
  }
}

export function parseBetslipsMessage(detail) {
  if (!detail || typeof detail !== 'object' || !detail.type) {
    return { type: 'script_error', error: 'Invalid or missing message detail' };
  }

  const { type, data, reason } = detail;
  const validTypes = ['betslips', 'partial', 'auth_error', 'api_error', 'script_error', 'captured'];
  const safeReason = BOOK_ANSWER_REASONS.includes(reason) ? reason : undefined;
  // The page can post any JSON, and an object whose toString is not callable throws
  // wherever it is stringified (#1292). Only strings (and a numeric stoppedAt) pass, and an
  // error keeps printable ASCII only, so the diagnostics panel shows what Sentry gets.
  const error = typeof detail.error === 'string'
    ? detail.error.replace(/[^\x20-\x7E]/g, '').slice(0, 200)
    : undefined;
  const stoppedAt = typeof detail.stoppedAt === 'number' ? detail.stoppedAt
    : typeof detail.stoppedAt === 'string' ? detail.stoppedAt.slice(0, 50)
    : undefined;

  if (!validTypes.includes(type)) {
    return { type: 'script_error', error: `Unknown message type: ${typeof type === 'string' ? type.slice(0, 50) : typeof type}` };
  }

  // Validate data field for types that carry betslip arrays (P1-2)
  if (type === 'betslips' || type === 'partial') {
    if (!Array.isArray(data)) {
      return { type: 'script_error', error: 'data is not an array' };
    }
    const cleaned = data.filter(b => b && typeof b === 'object');
    if (data.length > 0 && cleaned.length === 0) {
      return { type: 'script_error', error: `All ${data.length} entries filtered (non-object)` };
    }
    const out = { type, data: cleaned, error, stoppedAt, reason: safeReason };
    // #1714: DraftKings' page-2 JSON-RPC code, an int32 only.
    const c = detail.page2Code;
    if (Number.isInteger(c) && c >= -2147483648 && c <= 2147483647) out.page2Code = c;
    // #1712 PR A: a DK open-bet probe rides on the settled message. Invalid -> dropped, settled untouched.
    if (detail.openProbe !== undefined) {
      const probe = parseOpenProbe(detail.openProbe);
      if (probe) out.openProbe = probe;
      else out.openProbeDropped = true;
    }
    return out;
  }

  // #885 capture stage: the bridge synthesizes { type:'captured', pages, meta } after it
  // validated every page with parseCapturePage; re-check the container shape here.
  if (type === 'captured') {
    const pages = Array.isArray(detail.pages) ? detail.pages : null;
    const meta = detail.meta && typeof detail.meta === 'object' && !Array.isArray(detail.meta) ? detail.meta : null;
    if (!pages || !meta) return { type: 'script_error', error: 'captured payload malformed' };
    return { type: 'captured', pages, meta };
  }

  // 2026-10-18 visibility: what the book answered, bounded here because the page can post anything.
  if (type === 'auth_error') {
    return { type, data, error, stoppedAt, reason: safeReason, ...parseBookAnswer(detail) };
  }

  return { type, data, error, stoppedAt, reason: safeReason };
}

// A not-signed-in answer's status / content-type class / top-level key names. Strict: a status outside
// 100-599, an unknown class, or a key list that is not at most 10 names (a letter, then up to 39 of
// [A-Za-z0-9_]) is null.
const ANSWER_KEY_RE = /^[A-Za-z][A-Za-z0-9_]{0,39}$/; // a letter first: digit-only keys are dropped
const MAX_ANSWER_KEYS = 10;
const LIVE_ROUTES = ['history', 'signin', 'other'];
function parseBookAnswer(detail) {
  const { status, ctype, keys, route } = detail;
  const okKeys = Array.isArray(keys) && keys.length <= MAX_ANSWER_KEYS
    && keys.every((k) => typeof k === 'string' && ANSWER_KEY_RE.test(k));
  return {
    status: Number.isInteger(status) && status >= 100 && status <= 599 ? status : null,
    ctype: CONTENT_TYPE_CLASSES.includes(ctype) ? ctype : null,
    keys: okKeys ? keys.slice() : null,
    // The live route class when the answer was posted (closed list; anything else is 'other', which never counts).
    route: typeof route === 'string' && LIVE_ROUTES.includes(route) ? route : 'other',
  };
}

// #885: validate one capture_page posted by the response intercept (a book page can post
// anything). The intercept posts metadata already in the server's stored form (plan
// Post-PR-1 P2), so only those forms pass; the URL must be on the book's capture spec (A9).
// Bounded strings/numbers only; anything off-contract is dropped (null).
const HEADER_NAME_RE = /^[a-z0-9-]{1,50}$/;
const REGION_RE = /^[A-Z]{2}$/;
function storedFormContentType(ct) {
  if (ct === '' || ct === ':redacted') return true;
  const m = /^([a-z0-9!#$&^_.+/-]{1,100})(?:; charset=([a-z0-9._-]{1,20}))?$/.exec(ct);
  return !!m && CAPTURE_MEDIA_TYPES.includes(m[1]) && (m[2] === undefined || CAPTURE_CHARSETS.includes(m[2]));
}
function onCaptureSpec(url, spec) {
  if (!spec || !Array.isArray(spec.hosts) || typeof spec.pathPattern !== 'string') return false;
  let u;
  try { u = new URL(url); } catch (e) { return false; /* unparseable URL: page dropped (expected control flow) */ }
  return u.protocol === 'https:' && spec.hosts.includes(u.host) && new RegExp(spec.pathPattern).test(u.pathname);
}
export function parseCapturePage(page, spec) {
  if (!page || typeof page !== 'object' || Array.isArray(page)) return null;
  const { url, status, contentType, body, truncated, method, regionHeader, ts, headerNames } = page;
  if (typeof url !== 'string' || url.length > 300 || !url.startsWith('https://') || !onCaptureSpec(url, spec)) return null;
  if (!Number.isInteger(status) || status < 100 || status > 599) return null;
  if (typeof contentType !== 'string' || contentType.length > 100 || !storedFormContentType(contentType)) return null;
  if (typeof body !== 'string' || body.length > 200000) return null;
  if (typeof truncated !== 'boolean') return null;
  if (method !== 'GET' && method !== 'POST') return null;
  if (!(regionHeader === null || regionHeader === ':redacted' || (typeof regionHeader === 'string' && REGION_RE.test(regionHeader)))) return null;
  if (!Number.isInteger(ts) || ts < 0) return null;
  if (!Array.isArray(headerNames) || headerNames.length > 30
    || !headerNames.every((n) => typeof n === 'string' && HEADER_NAME_RE.test(n))) return null;
  return { url, status, contentType, body, truncated, method, regionHeader, ts, headerNames: headerNames.slice() };
}
